import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { projectsApi } from '../../api/projects.api'
import { useAuth } from '../../auth/AuthContext'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import styles from './ProjectTimeTracking.module.css'

function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s}s`
  return `${s}s`
}

/** "Allgemeiner Timer" oben auf der Projektseite: zeigt live, wer gerade an diesem Projekt
 * arbeitet (allgemein oder an einer konkreten Aufgabe) und wie lange insgesamt schon
 * erfasst wurde. Bewusst ohne fest einprogrammierte Namen — zeigt einfach jeden Nutzer mit
 * laufendem Timer an, momentan i.d.R. genau die zwei Teammitglieder des Projekts. */
export function ProjectTimeTracking({ projectId }: { projectId: string }) {
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const [, forceTick] = useState(0)

  // Re-rendert jede Sekunde, damit die live hochgezählte Anzeige aktuell bleibt, ohne den
  // Server dafür abzufragen — nur die Anzeige tickt, die Serverdaten bleiben unverändert
  // bis zum nächsten refetchInterval.
  useEffect(() => {
    const interval = setInterval(() => forceTick((t) => t + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  const activeTimersQuery = useQuery({
    queryKey: ['projects', projectId, 'time', 'active'],
    queryFn: async () => (await projectsApi.getActiveTimers(projectId)).data,
    refetchInterval: 20_000,
  })
  const summaryQuery = useQuery({
    queryKey: ['projects', projectId, 'time', 'summary'],
    queryFn: async () => (await projectsApi.getTimeSummary(projectId)).data,
    refetchInterval: 30_000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'time'] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'time', 'active'] })
  }

  const startMutation = useMutation({
    mutationFn: () => projectsApi.startTimer(projectId),
    onSuccess: invalidate,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Timer konnte nicht gestartet werden.'),
  })
  const stopMutation = useMutation({
    mutationFn: () => projectsApi.stopTimer(projectId),
    onSuccess: invalidate,
  })

  const activeTimers = activeTimersQuery.data ?? []
  const myGeneralTimer = activeTimers.find((t) => t.userId === me?.user.id && t.projectId === projectId)
  const myAnyActiveTimer = activeTimers.find((t) => t.userId === me?.user.id)

  const elapsedFor = (startedAt: string) => Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
  const liveTotal = (summaryQuery.data?.totalSeconds ?? 0) + activeTimers.reduce((sum, t) => sum + elapsedFor(t.startedAt), 0)

  return (
    <div className={styles.wrap}>
      <div className={styles.people}>
        {activeTimers.length === 0 ? (
          <span className={styles.empty}>Gerade läuft für niemanden ein Timer.</span>
        ) : (
          activeTimers.map((t) => (
            <div key={t.id} className={styles.person}>
              <Avatar name={t.user.displayName} imageUrl={t.user.avatarUrl} />
              <div>
                <div className={styles.personName}>{t.user.displayName}</div>
                <div className={styles.personDetail}>
                  {t.task ? `an „${t.task.title}"` : 'Allgemein'} · {formatDuration(elapsedFor(t.startedAt))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.controls}>
        {myGeneralTimer ? (
          <Button variant="danger" size="sm" loading={stopMutation.isPending} onClick={() => stopMutation.mutate()}>
            ⏸ Meinen Timer stoppen
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            loading={startMutation.isPending}
            disabled={Boolean(myAnyActiveTimer)}
            onClick={() => startMutation.mutate()}
          >
            ▶ Allgemeinen Timer starten
          </Button>
        )}
        {myAnyActiveTimer && !myGeneralTimer ? (
          <span className={styles.hint}>Du hast bereits einen laufenden Aufgaben-Timer — erst dort stoppen.</span>
        ) : null}
        <span className={styles.total}>
          Gesamtzeit: <strong>{formatDuration(liveTotal)}</strong>
        </span>
      </div>
    </div>
  )
}
